import { SellerGroupDTO } from './seller_group';
import { StaffDoor } from './staff';

export enum UserType {
  ROOT = 'ROOT',
  COMMON = 'COMMON',
}

export enum UserRole {
  ROOT = 'ROOT',
  ADMIN = 'ADMIN',
  COPRODUCER = 'COPRODUCER',
  EDITOR = 'EDITOR',
  SCANNER = 'SCANNER',
  SELLER = 'SELLER',
  USER = 'USER',
  CASHIER = 'CASHIER',
}

export const userPermissionsArray: UserRole[] = [
  UserRole.ADMIN,
  UserRole.COPRODUCER,
  UserRole.EDITOR,
  UserRole.SCANNER,
  UserRole.SELLER,
  UserRole.USER,
  UserRole.CASHIER,
];

// Roles that sell tickets marked "Sólo puede ser creada por administradores"
// (is_only_admin). The manual sale pages use the same set.
export const ADMIN_ONLY_TICKET_ROLES: UserRole[] = [UserRole.ROOT, UserRole.ADMIN, UserRole.COPRODUCER];

export const displayUserPermissions = {
  ADMIN: 'Administrador',
  COPRODUCER: 'Coproductor',
  EDITOR: 'Editor',
  SCANNER: 'Escaner',
  SELLER: 'Vendedor',
  USER: 'Usuario',
  CASHIER: 'Cajero',
};

export interface UserBusiness {
  id: string;
  business_id: string;
  user_id: string;
  active: boolean;
  permissions: UserRole;
  notification_emails_enabled: boolean;
  // % que cobra un Comisionista sobre lo que gana Avantti con esta productora.
  // Fracción (0.1 === 10%). > 0 es lo único que distingue a un Comisionista de
  // un Administrador común: el permiso subyacente sigue siendo ADMIN.
  commission_rate: number;
  // Caja asignada a un cajero. Vacía = ve todas las cajas de la productora.
  cash_register_id?: string | null;
}

export type UpdateBusinessUserPermissionsDTO = {
  id: string;
  active?: boolean;
  permissions?: UserRole;
  notification_emails_enabled?: boolean;
  commission_rate?: number;
};

/**
 * Etiqueta del rol de cara al usuario. "Comisionista" no es un permiso: es un
 * Admin con % cargado, así que la etiqueta depende del rate y no del enum.
 * `commissionRate` puede venir en fracción (0.1) o en porcentaje (10) — solo
 * importa que sea mayor a 0.
 */
export const displayRoleWithCommission = (permissions: UserRole, commissionRate?: number | string | null): string => {
  if (permissions === UserRole.ADMIN && Number(commissionRate || 0) > 0) {
    return 'Comisionista';
  }
  return displayUserPermissions[permissions as keyof typeof displayUserPermissions];
};

export const displayBusinessUserRole = (userBusiness: Pick<UserBusiness, 'permissions' | 'commission_rate'>): string => (
  displayRoleWithCommission(userBusiness.permissions, userBusiness.commission_rate)
);

export interface UserEvent {
  id: string;
  event_id: string;
  user_id: string;
  active: boolean;
  permissions: UserRole;
  seller_comission_rate: number;
  qr_count: number;
  qr_active: boolean;
  referral_id: string;
  user: UserDTO;
  alias: string;
  tag: string;
  seller_group_id: string;
  seller_group?: SellerGroupDTO;
  staff_door_id?: string | null;
  staff_door?: StaffDoor | null;
}

export interface AuthUserData {
  id: string;
  username: string,
  type: UserType,
  email: string,
  phone_number: string,
  user_businesses: UserBusiness[]
  user_events: UserEvent[]
  photo_url: string
  // Older cookies predate the field; treat missing as unknown, not verified.
  is_verified?: boolean
}
export default interface UserDTO {
  id: string;
  email: string;
  username: string;
  phone_number: string;
  firebaseId: string;
  type: UserType;
  is_verified: boolean;
  verification_code: string;
  user_businesses: UserBusiness[];
  user_events: UserEvent[];
  photo_url: string;
}

export type UserWithBusiness = UserDTO & { user_business: UserBusiness };
export type UserWithBusinessAndEvent = UserDTO & { user_business: UserBusiness, user_event: UserEvent };
